import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { Button, VerticalExpander, Text } from '../../../elementals';
import * as hooks from '../../../../hooks';
import * as coreUtils from '../../../../utils';
import CounterOfferCountdown from '../counterOfferCountdown';
import classnames from 'classnames/bind';
import _ from 'lodash';
import styles from './styles.scss';
import * as graph from '@adc/sdk';
import SealedBidForm from '../bidForm';
import { useSealedBidForm } from '../../../../utils/sealedBid';
import GeneralModal from '../../../../components/overlays/generalModal';
const cx = classnames.bind(styles);
const CounterOfferModalButton = ({ listing, segment, acceptButtonText = 'Accept Counteroffer', updateButtonText = 'Update My Offer', rejectText = 'Decline and Stand Firm', modalTitle, headers, bid, counterOffer, readOnly: readOnlyProp = true, canUpdateMyOffer = true, onAccept = () => Promise.resolve(), onDecline = () => Promise.resolve(), onCancel = () => false, onUpdate = () => Promise.resolve(), viewingFrom = graph.enums.CurrentTargetAudience.Seller, classNames = {}, hideRejectButton, hideCancelButton, buttonProps = {}, actions, preventClose = true, formRef, ...rest }) => {
    const [showModal, setShowModal] = React.useState(false);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [isDeclining, setDeclining] = React.useState(false);
    const { data, dirty, errors, config, readOnly, components, reset, toggleReadOnly, validate, } = useSealedBidForm({
        bid,
        headers,
        listing,
        segment,
        readOnly: readOnlyProp,
        counterOffer,
        canUpdateMyOffer,
        viewingFrom,
    });
    const breakpoint = hooks.useBreakpoints();
    const isMobile = breakpoint === 'mobile';
    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        reset();
    }, [reset]);
    const handleAccept = useCallback(async () => {
        setSubmitting(true);
        await onAccept();
        setSubmitting(false);
        handleCloseModal();
    }, [handleCloseModal, onAccept]);
    const handleCancel = useCallback(() => {
        handleCloseModal();
        onCancel();
    }, [handleCloseModal, onCancel]);
    const handleDecline = useCallback(async () => {
        setDeclining(true);
        await onDecline();
        setDeclining(false);
        handleCloseModal();
    }, [handleCloseModal, onDecline]);
    const handleUpdate = useCallback(async () => {
        if (!data || !validate())
            return;
        setSubmitting(true);
        await onUpdate({
            sealedBidInput: data,
        });
        setSubmitting(false);
        handleCloseModal();
    }, [data, validate, onUpdate, handleCloseModal]);
    const header = useMemo(() => {
        if (modalTitle)
            return modalTitle;
        return (_jsxs("div", { className: cx('modal-title'), children: [isMobile ? (_jsxs(_Fragment, { children: [_jsx(Text, { size: 'big', color: 'gray', weight: 'heavy', children: "Seller Counteroffer" }), counterOffer?.bid_amount && (_jsx(Text, { size: 'massive', color: 'gray', weight: 'heavy', children: coreUtils.formatters.formatMoney(counterOffer.bid_amount) })), _jsx(Text, { size: 'small', color: 'gray', children: "Take a look at the counteroffer in detail." })] })) : (_jsxs(_Fragment, { children: [_jsx(Text, { size: 'big', color: 'black', children: "Congratulations! You have received a Counteroffer!" }), _jsx(Text, { size: 'small', color: 'gray', children: "You can accept the counteroffer or update your offer" })] })), _jsx(CounterOfferCountdown, { className: cx('u-mt-2 u-mb-1'), bid: counterOffer })] }));
    }, [modalTitle, counterOffer]);
    const handleShowModal = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
    }, []);
    const actionButtons = useMemo(() => {
        if (actions)
            return actions;
        return [
            {
                label: rejectText,
                role: 'secondary',
                color: 'error',
                elmId: 'counteroffer_reject_button',
                style: { float: 'left' },
                onClick: handleDecline,
                isCountdownButton: true,
                reInitialize: true,
                disabled: isSubmitting || isDeclining,
                loading: isDeclining,
            },
            {
                label: 'Cancel',
                elmId: 'counteroffer_cancel_button',
                onClick: handleCancel,
            },
            {
                label: readOnly || !canUpdateMyOffer ? acceptButtonText : updateButtonText,
                role: 'primary',
                onClick: dirty && canUpdateMyOffer ? handleUpdate : handleAccept,
                disabled: isSubmitting ||
                    isDeclining ||
                    (canUpdateMyOffer && ((!readOnly && !dirty) || !_.isEmpty(errors))),
                loading: isSubmitting,
                elmId: 'counteroffer_confirm_button',
                style: { marginLeft: '.75em' },
                isCountdownButton: !readOnly,
                reInitialize: true,
            },
        ];
    }, [
        actions,
        acceptButtonText,
        canUpdateMyOffer,
        dirty,
        errors,
        isSubmitting,
        isDeclining,
        readOnly,
        rejectText,
        updateButtonText,
        handleAccept,
        handleCancel,
        handleDecline,
        handleUpdate,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', role: 'primary', ..._.omit(buttonProps, 'label'), onClick: handleShowModal, className: cx('large-fixed-button', classNames?.button), elmId: 'counter_offer_button', children: buttonProps?.label ?? 'Review Counteroffer' }), _jsx(GeneralModal, { ...rest, open: showModal, title: header, className: cx('counter-offer-modal', classNames?.modal, {
                    'counter-offer-modal-mobile': isMobile,
                }), elmId: 'counter_offer_elm_id', mobileTitleSkin: 'light', onClose: handleCancel, actions: actionButtons, size: 'large', preventClose: preventClose, children: _jsx(VerticalExpander, { children: _jsx(SealedBidForm, { className: classNames?.form, config: config, slots: {
                            components,
                        }, bid: bid, counterOffer: counterOffer, listing: listing, onToggleReadOnly: toggleReadOnly, onSubmit: handleUpdate, readOnly: readOnly, dirty: dirty, viewingFrom: viewingFrom, formRef: formRef }) }) })] }));
};
export default CounterOfferModalButton;
//# sourceMappingURL=index.js.map